/**
 * Waitlist page — renders the logged-in customer's Klaviyo back-in-stock
 * (waitlist) items as real theme product cards.
 *
 * One SecureBite call, with no params:
 *
 *   GET {waitlistEndpoint}
 *     → SecureBite looks up the logged-in customer's email (Shopify), then their
 *       Klaviyo profile_id, then calls Klaviyo GET /api/events with
 *       filter=and(equals(profile_id,…),equals(metric_id,<BIS metric>))
 *     → each event's properties carry the product (ProductID/VariantId/…)
 *
 * The route pins the filter, metric, paging and sort on the server, and the
 * Klaviyo private key never reaches the theme. The browser never sends an email,
 * profile_id or metric id, so it can only get this customer's own events. A
 * customer with no Klaviyo profile yet gets { data: [] }.
 *
 * Klaviyo's native BIS event carries a ProductID but no handle, so we resolve
 * every product id -> handle in ONE Storefront GraphQL `nodes` call (public
 * token, safe in the browser). Then per item we fetch
 * {product.url}?view=waitlist-card&variant=… to inject a real card.
 *
 * Only logged-in customers reach this (the section gates on `customer`), and the
 * route itself requires a logged-in customer.
 *
 * Data attributes (set by the section):
 *   data-events-endpoint    SecureBite waitlist route (default /apps/proxy/klaviyo-events)
 *   data-storefront-token   public Storefront API token (product id -> handle)
 *   data-storefront-api-version  Storefront API version (default 2025-10)
 *   data-card-view          product template view for a card (default waitlist-card)
 *
 * States: loading → (grid | empty | error).
 */
if (!customElements.get('waitlist-page')) {
  customElements.define(
    'waitlist-page',
    class WaitlistPage extends HTMLElement {
      connectedCallback() {
        this.waitlistEndpoint = this.dataset.eventsEndpoint || '/apps/proxy/klaviyo-events';
        this.storefrontToken = this.dataset.storefrontToken || '';
        this.storefrontApiVersion = this.dataset.storefrontApiVersion || '2025-10';
        this.cardView = this.dataset.cardView || 'waitlist-card';
        this.grid = this.querySelector('[data-waitlist-grid]');
        this.loadingEl = this.querySelector('[data-waitlist-loading]');
        this.emptyEl = this.querySelector('[data-waitlist-empty]');
        this.errorEl = this.querySelector('[data-waitlist-error]');
        this.ensureProductForm();
        this.load();
      }

      /*
        The cards inject <product-form> for their Add to cart, so that definition
        has to exist. It isn't loaded globally (only product-ish pages need it),
        and a <script> tag inside this page's markup would never run when the
        customer-dashboard app injects it — so load it here instead. A script
        element created in JS does execute, and <product-form> elements already in
        the DOM upgrade retroactively once the definition lands, so there's no
        race with the cards being rendered.
      */
      ensureProductForm() {
        if (customElements.get('product-form')) return;

        const src = this.dataset.productFormSrc;
        if (!src || document.querySelector(`script[data-waitlist-product-form="${src}"]`)) return;

        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        script.setAttribute('data-waitlist-product-form', src);
        document.head.appendChild(script);
      }

      show(el, visible) {
        if (el) el.hidden = !visible;
      }

      setState(state) {
        this.show(this.loadingEl, state === 'loading');
        this.show(this.emptyEl, state === 'empty');
        this.show(this.errorEl, state === 'error');
        this.show(this.grid, state === 'grid');
      }

      // SecureBite routes are same-origin app-proxy paths (/apps/proxy/…).
      getJSON(url) {
        return fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((r) => {
          if (!r.ok) throw new Error(`${url} → ${r.status}`);
          return r.json();
        });
      }

      async load() {
        this.setState('loading');
        try {
          // The customer's back-in-stock events. SecureBite works out who the
          // customer is on the server, so nothing is sent from here.
          const events = await this.getJSON(this.waitlistEndpoint);
          const rows = (events && Array.isArray(events.data)) ? events.data : [];

          const items = this.itemsFromEvents(rows);
          if (!items.length) { this.setState('empty'); return; }

          await this.renderCards(items);
        } catch (e) {
          console.error('[waitlist]', e);
          this.setState('error');
        }
      }

      // Turn raw Klaviyo events into { handle?, variant_id?, product_id? }, deduped
      // per variant (a waitlist is per-size). Klaviyo's Shopify BIS events use
      // "VariantId" (lowercase d), "ProductID", "Handle"/"URL" (sometimes).
      itemsFromEvents(rows) {
        const seen = new Set();
        const items = [];
        for (const ev of rows) {
          const p = (ev.attributes && (ev.attributes.event_properties || ev.attributes.properties)) || {};
          const handleRaw = p.Handle || p.handle || p['Product Handle'] || p.product_handle || null;
          const url = p.URL || p.url || p['Product URL'] || null;
          let handle = handleRaw;
          if (!handle && typeof url === 'string') {
            const m = url.match(/\/products\/([^/?#]+)/);
            if (m) handle = decodeURIComponent(m[1]);
          }
          const variantId = p.VariantId || p.VariantID || p.variant_id || p['Variant ID'] || p.variant || null;
          const productId = p.ProductID || p.product_id || p['Product ID'] || null;
          const key = variantId || handle || productId;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          items.push({ handle: handle || null, variant_id: variantId || undefined, product_id: productId || undefined });
        }
        return items;
      }

      // Resolve each waited-on VARIANT via one Storefront GraphQL `nodes` call.
      // Resolving by variant id (not just product id) also validates the item:
      // the Storefront API returns null for a variant whose product is
      // unpublished/deleted OR whose variant was removed in admin — so those
      // items get dropped instead of rendering the wrong (fallback) variant.
      // We take the product handle straight from the resolved variant.
      // The Storefront token is public, so this is safe in the browser.
      async resolveHandles(items) {
        const withVariant = items.filter((it) => it.variant_id);
        // No token or nothing to resolve: can't validate variants, so keep only
        // items that already carry a handle (e.g. parsed from an event URL).
        if (!withVariant.length || !this.storefrontToken) {
          return items.filter((it) => it.handle);
        }

        const ids = [...new Set(withVariant.map((it) => String(it.variant_id)))];
        const gids = ids.map((id) => `gid://shopify/ProductVariant/${id}`);
        const query =
          'query($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id product{handle}}}}';
        const handleByVariant = {};
        try {
          const res = await fetch(
            `${window.location.origin}/api/${this.storefrontApiVersion}/graphql.json`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.storefrontToken,
              },
              body: JSON.stringify({ query, variables: { ids: gids } }),
            }
          );
          if (!res.ok) return items; // API hiccup: don't drop everything
          const data = await res.json();
          for (const node of (data && data.data && data.data.nodes) || []) {
            if (node && node.id && node.product && node.product.handle) {
              const m = node.id.match(/ProductVariant\/(\d+)/);
              if (m) handleByVariant[m[1]] = node.product.handle;
            }
          }
        } catch (e) {
          console.warn('[waitlist] variant resolution failed', e);
          return items; // network error: don't drop everything
        }

        // Keep only items whose variant resolved (product published + variant
        // still exists); attach the fresh handle. Items with a handle but no
        // variant id (rare, from an event URL) are kept as-is.
        return items.filter((it) => {
          if (it.variant_id) {
            const handle = handleByVariant[String(it.variant_id)];
            if (!handle) return false; // unpublished/deleted product or variant
            it.handle = handle;
            return true;
          }
          return !!it.handle;
        });
      }

      cardUrlFor(item) {
        const root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
        const base = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(item.handle)}`;
        const params = new URLSearchParams({ view: this.cardView });
        if (item.variant_id) params.set('variant', item.variant_id);
        return `${base}?${params.toString()}`;
      }

      fetchCard(item) {
        if (!item.handle) return Promise.resolve(null);
        return fetch(this.cardUrlFor(item), { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : null))
          .then((html) => {
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.querySelector('[data-waitlist-item]');
          })
          .catch(() => null);
      }

      async renderCards(items) {
        const resolved = await this.resolveHandles(items);
        const cards = await Promise.all(resolved.map((it) => this.fetchCard(it)));
        const frag = document.createDocumentFragment();
        cards.forEach((card) => { if (card) frag.appendChild(card); });
        if (!frag.childNodes.length) { this.setState('empty'); return; }
        this.grid.innerHTML = '';
        this.grid.appendChild(frag);
        this.setState('grid');
      }
    }
  );
}
